import { Popover } from "@base-ui-components/react/popover"
import { AlarmClock, CalendarClock, CalendarDays, CalendarRange, Clock3, Loader2, Sunrise } from "lucide-react"
import { observer } from "mobx-react"
import { useMemo, useState } from "react"
import { usePortalContainer } from "../hooks/usePortalContainer"
import { useCodeStore } from "../store/context"
import type { TaskModel } from "../store/TaskModel"
import { parseLocalSnoozeTime, snoozePresets, toLocalSnoozeInputValue, type SnoozePreset } from "../utils/taskSnooze"

const exactTimeFormatter = new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
})

function PresetIcon({ id }: { id: SnoozePreset["id"] }): JSX.Element {
    if (id === "one-hour") return <Clock3 size={17} />
    if (id === "later-today") return <AlarmClock size={17} />
    if (id === "tomorrow-morning") return <Sunrise size={17} />
    if (id === "friday-afternoon") return <CalendarClock size={17} />
    if (id === "one-month") return <CalendarRange size={17} />
    return <CalendarDays size={17} />
}

export const TaskSleepButton = observer(function TaskSleepButton({
    taskModel,
    onSlept,
}: {
    taskModel: Pick<TaskModel, "id" | "isClosed" | "isSleeping" | "primarySessionThreadReference">
    onSlept: () => void
}): JSX.Element | null {
    const codeStore = useCodeStore()
    const portalContainer = usePortalContainer()
    const [open, setOpen] = useState(false)
    const [openedAt, setOpenedAt] = useState(() => new Date())
    const [customValue, setCustomValue] = useState("")
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const presets = useMemo(() => snoozePresets(openedAt), [openedAt])
    const hasSession = taskModel.primarySessionThreadReference !== null
    const disabled = !hasSession || submitting

    if (taskModel.isClosed) return null

    const handleOpenChange = (nextOpen: boolean) => {
        if (nextOpen) {
            const now = new Date()
            setOpenedAt(now)
            setCustomValue(toLocalSnoozeInputValue(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 9, 0, 0, 0)))
            setError(null)
        }
        setOpen(nextOpen)
    }

    const finish = () => {
        setOpen(false)
        onSlept()
    }

    const sleepUntil = async (until: string) => {
        setSubmitting(true)
        setError(null)
        try {
            await codeStore.tasks.sleepTask(taskModel.id, until)
            finish()
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : "Could not put this task to sleep")
        } finally {
            setSubmitting(false)
        }
    }
    const wake = async () => {
        setSubmitting(true)
        setError(null)
        try {
            await codeStore.tasks.wakeTask(taskModel.id)
            setOpen(false)
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : "Could not wake this task")
        } finally {
            setSubmitting(false)
        }
    }

    const submitCustom = () => {
        const at = parseLocalSnoozeTime(customValue)
        if (!at || at.getTime() < Date.now() + 60_000) {
            setError("Choose a time at least one minute in the future.")
            return
        }
        void sleepUntil(at.toISOString())
    }

    const disabledReason = !hasSession ? "This task does not have a resumable session" : "Sleep until"

    return (
        <Popover.Root open={open} onOpenChange={handleOpenChange}>
            <Popover.Trigger
                className="btn flex h-9 flex-shrink-0 items-center justify-center gap-2 bg-base-100 px-3 text-sm font-medium text-base-content hover:bg-base-300 disabled:cursor-not-allowed disabled:opacity-40"
                title={disabledReason}
                aria-label={disabledReason}
                disabled={disabled}
            >
                <AlarmClock size={14} />
                <span>Sleep until</span>
            </Popover.Trigger>
            <Popover.Portal container={portalContainer}>
                <Popover.Positioner side="top" align="end" sideOffset={8} collisionPadding={8}>
                    <Popover.Popup className="z-50 w-[min(24rem,calc(100vw-1rem))] border border-border bg-base-100 shadow-lg outline-none">
                        <div className="border-b border-border px-3 py-2.5">
                            <div className="text-sm font-semibold text-base-content">Sleep until</div>
                            <div className="mt-0.5 text-xs text-muted">Set this task aside and bring it back when you choose.</div>
                        </div>

                        {taskModel.isSleeping && (
                            <button
                                type="button"
                                className="btn min-h-11 w-full border-b border-border px-3 text-left text-sm text-primary hover:bg-base-200"
                                disabled={submitting}
                                onClick={() => void wake()}
                            >
                                Wake now
                            </button>
                        )}
                        <div className="grid grid-cols-2 gap-px bg-border">
                            {presets.map((preset) => {
                                return (
                                    <button
                                        key={preset.id}
                                        type="button"
                                        className="btn flex min-h-20 flex-col items-start justify-between bg-base-100 p-3 text-left text-base-content hover:bg-base-200 disabled:cursor-not-allowed disabled:opacity-40"
                                        onClick={() => void sleepUntil(preset.at.toISOString())}
                                        disabled={submitting}
                                    >
                                        <PresetIcon id={preset.id} />
                                        <span>
                                            <span className="block text-sm font-medium">{preset.label}</span>
                                            <span className="mt-0.5 block text-[11px] text-muted">{exactTimeFormatter.format(preset.at)}</span>
                                        </span>
                                    </button>
                                )
                            })}
                        </div>

                        <button
                            type="button"
                            className="btn min-h-11 w-full border-t border-border px-3 text-left text-sm text-base-content hover:bg-base-200"
                            disabled={submitting}
                            onClick={() => void sleepUntil("indefinite")}
                        >
                            Until I wake it
                        </button>
                        <div className="flex items-end gap-2 border-t border-border px-3 py-3">
                            <label className="min-w-0 flex-1 text-xs text-muted">
                                Wake at
                                <input
                                    type="datetime-local"
                                    className="mt-1 h-9 w-full border border-border bg-input px-2 text-sm text-base-content outline-none focus:border-primary"
                                    value={customValue}
                                    min={toLocalSnoozeInputValue(new Date(openedAt.getTime() + 2 * 60_000))}
                                    onChange={(event) => setCustomValue(event.target.value)}
                                    disabled={submitting}
                                />
                            </label>
                            <button
                                type="button"
                                className="btn flex h-9 items-center justify-center bg-primary px-3 text-sm font-medium text-primary-content hover:bg-primary/90 disabled:opacity-50"
                                onClick={submitCustom}
                                disabled={submitting || customValue.length === 0}
                            >
                                {submitting ? <Loader2 size={14} className="animate-spin" /> : "Sleep"}
                            </button>
                        </div>

                        {error && (
                            <div role="alert" className="border-t border-error/40 bg-error/10 px-3 py-2 text-xs text-error">
                                {error}
                            </div>
                        )}
                    </Popover.Popup>
                </Popover.Positioner>
            </Popover.Portal>
        </Popover.Root>
    )
})
